import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardTile from './DashboardTile';
import InfoTooltip from '@/components/InfoTooltip';
import type { UserProfile } from '@/types/dashboard';

interface ProfileTileProps {
  profile: UserProfile;
}

const ProfileTile: FC<ProfileTileProps> = ({ profile }) => {
  const navigate = useNavigate();
  const initials = profile.name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <DashboardTile wide onClick={() => navigate('/perfil')} data-testid="profile-tile" data-tour="profile-tile">
      <div className="flex items-center gap-4">
        {/* Avatar */}
        <div
          className="w-12 h-12 rounded-full shrink-0 flex items-center justify-center text-sm font-bold overflow-hidden"
          style={{
            border: '2px solid var(--tile-accent)',
            background: profile.avatar ? 'transparent' : 'var(--tile-bar-bg)',
            color: 'var(--tile-accent)',
          }}
        >
          {profile.avatar ? (
            <img src={profile.avatar} alt={profile.name} className="w-full h-full object-cover" />
          ) : (
            initials
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="font-bold text-[15px] truncate" style={{ color: 'var(--tile-text-primary)' }}>
            {profile.name}
          </p>
          <p className="text-xs font-medium flex items-center" style={{ color: 'var(--tile-accent)' }}>
            Nível {profile.level} — {profile.level_name}
            <InfoTooltip termKey="level" iconOnly />
          </p>
        </div>
      </div>

      {/* XP Progress bar */}
      <div className="mt-4">
        <div
          className="w-full h-2 rounded-full overflow-hidden"
          style={{ background: 'var(--tile-bar-bg)' }}
        >
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${profile.xp_progress_percent}%`,
              background: 'var(--tile-accent)',
            }}
          />
        </div>
        <div className="flex justify-between mt-1.5">
          <span className="text-2xs font-medium flex items-center" style={{ color: 'var(--tile-text-secondary)' }}>
            {profile.total_xp.toLocaleString('pt-BR')} / {profile.xp_for_next_level.toLocaleString('pt-BR')} XP
            <InfoTooltip termKey="xp" iconOnly />
          </span>
          <span className="text-2xs font-medium" style={{ color: 'var(--tile-text-muted)' }}>
            Nível {profile.level + 1}
          </span>
        </div>
      </div>
    </DashboardTile>
  );
};

export default ProfileTile;
